// src/hooks/useClarityTracking.ts
// Hook for tracking user sessions and events with Microsoft Clarity

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import clarityService from '../services/clarityService';
import useAuth from '../features/auth/hooks/useAuth';

/**
 * Custom hook to automatically track user sessions with Clarity
 * - Identifies logged-in users
 * - Sets custom tags (account type, etc.)
 * - Tracks page views
 */
export const useClarityTracking = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  // Identify user when they log in
  useEffect(() => {
    if (isAuthenticated && user && clarityService.isInitialized()) {
      // Identify user with their ID and email
      clarityService.identify(
        user.id,
        undefined, // customSessionId - auto-generated
        location.pathname, // customPageId
        user.email // friendlyName
      );

      // Set custom tags for filtering in Clarity dashboard
      clarityService.setTag('accountType', user.accountType || 'Free Plan');
      clarityService.setTag('userEmail', user.email);

      if (user.firstName && user.lastName) {
        clarityService.setTag('userName', `${user.firstName} ${user.lastName}`);
      }
    }
  }, [isAuthenticated, user, location.pathname]);

  // Track page views
  useEffect(() => {
    if (clarityService.isInitialized()) {
      // Update page ID on route change
      if (isAuthenticated && user) {
        clarityService.identify(
          user.id,
          undefined,
          location.pathname
        );
      }
    }
  }, [location.pathname, isAuthenticated, user]);

  return clarityService;
};

export default useClarityTracking;
